// @flow
import React from 'react';
import {Dropdown, DropdownItem} from '@homeaway/react-dropdown';
import {injectIntl, type IntlShape, defineMessages} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';
import routerActions from '../../actions/router';

import './SortDropDown.less';
import {GENERIC_EVENT, trigger as edapTrigger} from '../../utils/edapWrapper';

type Props = {
    intl: IntlShape,
    filter: Function,
    orderBy: ?string,
    ascending: ?boolean,
    siteName: string
};

const messages = defineMessages({
    sort: {
        id: 'sort',
        defaultMessage: 'Sort'
    },
    default: {
        id: 'brandedDefaultSort',
        defaultMessage:
            '{siteName, select, vrbo {VRBO Sort} other {HomeAway Sort}}'
    },
    priceAscending: {
        id: 'priceAscendingSort',
        defaultMessage: 'Price: Low to High'
    },
    priceDescending: {
        id: 'priceDescendingSort',
        defaultMessage: 'Price: High to Low'
    },
    averageRating: {
        id: 'averageRatingSort',
        defaultMessage: 'Guest Rating'
    },
    availabilityUpdated: {
        id: 'availabilityUpdatedSort',
        defaultMessage: 'Availability Last Updated'
    },
    bedroomsAscending: {
        id: 'bedroomsAscendingSort',
        defaultMessage: 'Bedrooms: Fewest to Most'
    },
    bedroomsDescending: {
        id: 'bedroomsDescendingSort',
        defaultMessage: 'Bedrooms: Most to Fewest'
    },
    demandConversion: {
        id: 'demandConversionSort',
        defaultMessage: 'Most Popular'
    },
    instantConfirmation: {
        id: 'instantConfirmationSort',
        defaultMessage: 'Instant Confirmation'
    },
    reviews: {
        id: 'reviewsSort',
        defaultMessage: 'Number of Reviews'
    }
});

type SortOption = {
    message: any,
    orderBy: string,
    ascending: boolean
};

const sortOptions: Array<SortOption> = [
    {
        message: messages.default,
        orderBy: 'default',
        ascending: true
    },
    {
        message: messages.priceAscending,
        orderBy: 'prices',
        ascending: true
    },
    {
        message: messages.priceDescending,
        orderBy: 'prices',
        ascending: false
    },
    {
        message: messages.averageRating,
        orderBy: 'averageRating',
        ascending: false
    },
    {
        message: messages.availabilityUpdated,
        orderBy: 'availabilityUpdated',
        ascending: false
    },
    {
        message: messages.bedroomsAscending,
        orderBy: 'bedrooms',
        ascending: true
    },
    {
        message: messages.bedroomsDescending,
        orderBy: 'bedrooms',
        ascending: false
    },
    // SERP-1487: By command of Lord Biz this sort option is confusing and need to be disabled
    // {
    //     message: messages.demandConversion,
    //     orderBy: 'mostPopular',
    //     ascending: true
    // },
    {
        message: messages.instantConfirmation,
        orderBy: 'instantConfirmation',
        ascending: true
    },
    {
        message: messages.reviews,
        orderBy: 'travelerReviewCount',
        ascending: false
    }
];

const mapStateToProps = ({searchParams: {params}, system: {siteName}}) => ({
    orderBy: params.orderBy,
    ascending: !!params.ascending,
    siteName
});

const mapDispatchToProps = {
    filter: routerActions.filter
};

export const SortDropDown = (props: Props) => {
    const {intl, filter, siteName} = props;
    const createHandler = (orderBy, ascending) => {
        return (e) => {
            e.preventDefault();

            edapTrigger(GENERIC_EVENT, {
                eventaction: 'sort selected'
            });

            filter({
                orderBy,
                ascending
            });
        };
    };

    const selectedIndex = Math.max(
        sortOptions.findIndex(
            (option) =>
                option.orderBy === props.orderBy &&
                option.ascending === props.ascending
        ),
        0
    );

    const displayMessage =
        selectedIndex === 0
            ? messages.sort
            : sortOptions[selectedIndex].message;

    return (
        <div className="SortDropDown">
            <Dropdown
                id="sortDropdown"
                label={intl.formatMessage(displayMessage, {
                    siteName
                })}
                dropdownRight
            >
                {sortOptions.map((link, index) => (
                    <DropdownItem
                        key={index}
                        text={intl.formatMessage(link.message, {
                            siteName
                        })}
                        data-wdio={`${link.orderBy}:${String(link.ascending)}`}
                        className={
                            selectedIndex === index
                                ? 'SortDropDownItem__selected'
                                : ''
                        }
                        onClick={createHandler(link.orderBy, link.ascending)}
                        link="#"
                    />
                ))}
            </Dropdown>
        </div>
    );
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(SortDropDown);
